import React, { useDeferredValue, useEffect, useState } from 'react';

import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Close, EditCalendar, Notes, Person, Remove, Schedule, Star } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link as LinkMui,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  clamp,
  compact,
  concat,
  filter,
  find,
  get,
  includes,
  isEmpty,
  isNil,
  join,
  map,
  max,
  noop,
  pick,
  reduce,
  snakeCase,
  some,
  toString,
  uniq,
} from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-multi-date-picker';
import { Link, Navigate } from 'react-router-dom';
import * as Yup from 'yup';
import type { SubmitHandler } from 'react-hook-form';
import type { DateObject } from 'react-multi-date-picker';
import numeral from 'numeral';
import {
  BottomSafeArea,
  LoadingButton,
  LoadingSpinner,
  NumberTextField,
  RadioButtonGroup,
  useGoBack,
  UniformCard,
  useRestaurantCashbacks,
  useTrackPageView,
} from '../../../components';
import { RestaurantSelector } from '../../../components/nav_bar/restaurant_selector';
import { AppContext } from '../../../contexts/app_context';
import { createScopedI18n, i18n } from '../../../i18n/i18n';
import { hookFormNestedFieldErrorMessage, joinPairs, labelFieldEnum } from '../../../utils/libs';
import Protector from '../../../components/protector';
import { CreditNotEnoughPopup } from '../../../components/credit_not_enough_popup';
import Snackbar, { INSUFFICIENT_BALANCE } from '../../../utils/snackbar';
import Stash, { CREATE_JOB } from '../../../utils/stash';
import { buildIdempotencyKey } from '../../../utils/idempotency';
import { filterAvailableCashbacks } from '../../../components/cashback_info';

const jobCreatePageI18n = createScopedI18n('pages.jobs_new');
const jobFieldI18n = createScopedI18n('graphql.fields.job');
const jobErrorI18n = createScopedI18n('graphql.errors.types.job.fields', { joinOutput: true });
const walletErrorI18n = createScopedI18n('graphql.errors.types.wallet.fields', { joinOutput: true });

const MAX_SCHEDULE_DAYS = 12;

const JobCreateSchema = Yup.object({
  scheduleDates: Yup.array()
    .of(
      Yup.lazy(() =>
        Yup.date()
          .max(
            dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days'),
            jobErrorI18n('schedule_date.too_long', { count: MAX_SCHEDULE_DAYS }),
          )
          .required(jobErrorI18n('schedule_date.blank')),
      ),
    )
    .required(jobErrorI18n('schedule_dates.blank'))
    .min(1, jobErrorI18n('schedule_dates.too_short', { count: 1 })),
  startTime: Yup.date()
    .typeError(jobErrorI18n('job_start_at.invalid'))
    .required(jobErrorI18n('job_start_at.blank'))
    .test({ message: jobErrorI18n('job_start_at.invalid'), test: (startTime) => startTime.getMinutes() % 30 === 0 }),
  endTime: Yup.date()
    .typeError(jobErrorI18n('job_end_at.invalid'))
    .required(jobErrorI18n('job_end_at.blank'))
    .test({ message: jobErrorI18n('job_end_at.invalid'), test: (endTime) => endTime.getMinutes() % 30 === 0 }),
  breakDurationMinute: Yup.number()
    .required(jobErrorI18n('break_duration.blank'))
    .min(0, jobErrorI18n('break_duration.too_short', { count: 0 })),
  gender: Yup.string()
    .required(jobErrorI18n('gender.blank'))
    .oneOf(['male', 'female', 'SET_NULL_ON_SUBMIT'], jobErrorI18n('gender.invalid')),
  jobType: Yup.string().required(jobErrorI18n('job_type.blank')),
  duties: Yup.array()
    .of(Yup.string().required(jobErrorI18n('duty.blank')))
    .required(jobErrorI18n('duty.blank'))
    .when('jobType', ([jobType], schema) => {
      if (isNil(jobType) || jobType === 'other') {
        return schema;
      }
      return schema.min(1, jobErrorI18n('duty.blank'));
    }),
  remark: Yup.string()
    .nullable()
    .when('jobType', ([jobType], schema) =>
      isNil(jobType) || jobType === 'other' ? schema.required(jobErrorI18n('remark.blank')) : schema,
    ),
  detail: Yup.string().nullable(),
  numberOfPosition: Yup.number()
    .required(jobErrorI18n('number_of_position.blank'))
    .min(1, jobErrorI18n('number_of_position.too_short', { count: 1 })),
  uniformId: Yup.string().required(jobErrorI18n('uniform.blank')),
  uniform: Yup.string()
    .nullable()
    .when('uniformId', ([uniformId], schema) => {
      if (uniformId === '_OTHER_UNIFORM') {
        return schema.required(jobErrorI18n('uniform.blank'));
      }
      return schema;
    }),
  benefit: Yup.string().required(jobErrorI18n('benefit.blank')),
  skillIds: Yup.array().of(Yup.string()),
});

const mapValueKeyToI18nField = (valueKey: string) => {
  const valueKeyI18nFieldMap: { [valueKey: string]: string } = {
    scheduleDates: 'schedule_dates',
    startTime: 'start_time',
    endTime: 'end_time',
    breakDurationMinute: 'break_duration_minute',
    gender: 'gender',
    jobType: 'job_type',
    duties: 'duty',
    remark: 'remark',
    detail: 'detail',
    numberOfPosition: 'number_of_position',
    uniform: 'uniform',
    benefit: 'benefit',
    restaurantBidPricePerHour: 'restaurantBidPricePerHour',
    skills: 'skills',
  };

  return valueKeyI18nFieldMap[valueKey] ?? snakeCase(valueKey);
};

export type BidPriceOptionType = 'BID_PRICE_OPTION_DEFAULT' | 'BID_PRICE_OPTION_CUSTOM';

interface JobCreateFormData {
  scheduleDates: Date[];
  startTime: Date;
  endTime: Date;
  breakDurationMinute: number;
  gender: string;
  jobType: string;
  duties: string[];
  remark: string | null | undefined;
  detail: string | null | undefined;
  numberOfPosition: number;
  uniformId: string;
  uniform: string | null | undefined;
  benefit: string;
  restaurantBidPricePerHour: number | null;
  skillIds: string[];
}

/* Note: Job Creating Option Sample Data
JobCreatingOption: 
  {
    "maxNumberOfPosition": -1,
    "otHour": 8,
    "pricing": [
      {
        "id": "restaurant_hiring_same_day_weekday",
        "price": 65,
        "priceType": "restaurant_hiring_same_day_weekday",
      },
      {
        "id": "restaurant_hiring_same_day_weekend",
        "price": 70,
        "priceType": "restaurant_hiring_same_day_weekend",
      },
      {
        "id": "restaurant_hiring_same_day_holiday",
        "price": 72,
        "priceType": "restaurant_hiring_same_day_holiday",
      },
      {
        "id": "restaurant_hiring_same_day_special",
        "price": 72,
        "priceType": "restaurant_hiring_same_day_special",
      },
      {
        "id": "restaurant_hiring_next_day_weekday",
        "price": 65,
        "priceType": "restaurant_hiring_next_day_weekday",
      },
      {
        "id": "restaurant_hiring_next_day_weekend",
        "price": 70,
        "priceType": "restaurant_hiring_next_day_weekend",
      },
      {
        "id": "restaurant_hiring_next_day_holiday",
        "price": 72,
        "priceType": "restaurant_hiring_next_day_holiday",
      },
      {
        "id": "restaurant_hiring_next_day_special",
        "price": 72,
        "priceType": "restaurant_hiring_next_day_special",
      },
      {
        "id": "restaurant_hiring_same_day_weekday_ot",
        "price": 97.5,
        "priceType": "restaurant_hiring_same_day_weekday_ot",
      },
      {
        "id": "restaurant_hiring_same_day_weekend_ot",
        "price": 105,
        "priceType": "restaurant_hiring_same_day_weekend_ot",
      },
      {
        "id": "restaurant_hiring_same_day_holiday_ot",
        "price": 108,
        "priceType": "restaurant_hiring_same_day_holiday_ot",
      },
      {
        "id": "restaurant_hiring_same_day_special_ot",
        "price": 108,
        "priceType": "restaurant_hiring_same_day_special_ot",
      },
      {
        "id": "restaurant_hiring_next_day_weekday_ot",
        "price": 97.5,
        "priceType": "restaurant_hiring_next_day_weekday_ot",
      },
      {
        "id": "restaurant_hiring_next_day_weekend_ot",
        "price": 105,
        "priceType": "restaurant_hiring_next_day_weekend_ot",
      },
      {
        "id": "restaurant_hiring_next_day_holiday_ot",
        "price": 108,
        "priceType": "restaurant_hiring_next_day_holiday_ot",
      },
      {
        "id": "restaurant_hiring_next_day_special_ot",
        "price": 108,
        "priceType": "restaurant_hiring_next_day_special_ot",
      }
    ],
    "cancelFee": [
      {
        "id": "restaurant_cancel_normal",
        "price": 50,
        "priceType": "restaurant_cancel_normal",
      },
      {
        "id": "restaurant_cancel_same_day",
        "price": 200,
        "priceType": "restaurant_cancel_same_day",
      },
      {
        "id": "restaurant_cancel_next_day",
        "price": 100,
        "priceType": "restaurant_cancel_next_day",
      }
    ],
    "enumJobTypesAndDuties": [
      {
        "id": "pc",
        "duties": [
          "sales",
          "tasting",
          "recreation",
          "storing"
        ],
        "defaultDuties": [
          "sales"
        ],
      },
      {
        "id": "staff",
        "duties": [
          "lifting",
          "storing",
          "reception",
          "facilitator"
        ],
        "defaultDuties": [
          "facilitator"
        ],
      },
      {
        "id": "other",
        "duties": [],
        "defaultDuties": [],
      }
    ],
    "pricingRange": [
      {
        "id": "restaurant_hiring_normal_weekday",
        "min": 60,
        "max": 62,
        "priceType": "restaurant_hiring_normal_weekday",
      },
      {
        "id": "restaurant_hiring_normal_weekend",
        "min": 65,
        "max": 69,
        "priceType": "restaurant_hiring_normal_weekend",
      },
      {
        "id": "restaurant_hiring_normal_holiday",
        "min": 69,
        "max": 71,
        "priceType": "restaurant_hiring_normal_holiday",
      },
      {
        "id": "restaurant_hiring_normal_special",
        "min": 60,
        "max": 71,
        "priceType": "restaurant_hiring_normal_special",
      },
      {
        "id": "restaurant_hiring_normal_weekday_ot",
        "min": 90,
        "max": 93,
        "priceType": "restaurant_hiring_normal_weekday_ot",
      },
      {
        "id": "restaurant_hiring_normal_weekend_ot",
        "min": 97.5,
        "max": 103.5,
        "priceType": "restaurant_hiring_normal_weekend_ot",
      },
      {
        "id": "restaurant_hiring_normal_holiday_ot",
        "min": 103.5,
        "max": 106.5,
        "priceType": "restaurant_hiring_normal_holiday_ot",
      },
      {
        "id": "restaurant_hiring_normal_special_ot",
        "min": 90,
        "max": 106.5,
        "priceType": "restaurant_hiring_normal_special_ot",
      }
    ],
    "skills": [
      {
        "id": "1",
        "name": "แนะนำสินค้า",
        "description": "ให้ข้อมูลสินค้า แนะนำสินค้า และโปรโมชั่นสินค้าได้ ",
        "additionalWagePerHour": 5.0
      }
    ],
    "uniforms": [
      {
        "id": "5",
        "title": "ตี้น้อย พนังงานเสิร์ฟ",
        "jobType": "serve",
        "photo": "https://public-storage-dev.temp.in.th/uniforms/uniform_5.oQ_D.regular.jpg"
      },
    ]
  }
*/

const jobCreatingOptionGql = gql(`
  query jobCreatingOption($businessType: BUSINESS_TYPE_OPTIONS!) {
    jobCreatingOption(businessType: $businessType) {
      maxNumberOfPosition
      otHour
      pricing {
        id
        price
        priceType
      }
      cancelFee {
        id
        price
        priceType
      }
      enumJobTypesAndDuties {
        id
        duties
        defaultDuties
      }
      pricingRange {
        id
        min
        max
        priceType
      }
      skills {
        id
        name
        description
        additionalWagePerHour
      }
      uniforms {
        id
        title
        jobType
        photo
      }
    }
  }
`);

const jobPricing = gql(`
  query jobPricing($requirements: [JobPricingInput!]!) {
    jobPricing(requirements: $requirements) {
      date
      minPricePerHour
      maxPricePerHour
      minNormalPricePerHour
      maxNormalPricePerHour
      minOtPricePerHour
      maxOtPricePerHour
      minTotalPrice
      maxTotalPrice
      operationMinute
      otMinute
      normalMinute
      minBidAllowed
    }
  }
`);

const createJobGql = gql(`
  mutation createJob(
    $restaurantId: ID
    $benefit: String
    $breakDurationMinute: Int!
    $numberOfPosition: Int!
    $duties: JSON
    $endTime: Time!
    $jobType: String
    $gender: GENDER_OPTIONS
    $remark: String
    $scheduleDate: Date!
    $startTime: Time!
    $restaurantBidPricePerHour: Float
    $uniformId: ID
    $skillIds: [ID!]
  ) {
    createJob(
      restaurantId: $restaurantId
      benefit: $benefit
      breakDurationMinute: $breakDurationMinute
      duties: $duties
      endTime: $endTime
      jobTypeV2: $jobType
      gender: $gender
      remark: $remark
      scheduleDate: $scheduleDate
      startTime: $startTime
      numberOfPosition: $numberOfPosition
      restaurantBidPricePerHour: $restaurantBidPricePerHour
      uniformId: $uniformId
      skillIds: $skillIds
    ) {
      success
      errors
    }
  }
`);

const hasUrgentJob = (scheduleDates: Date[]) => {
  const tomorrowEndDay = dayjs().add(1, 'day').endOf('day');
  return some(scheduleDates, (scheduleDate) => dayjs(scheduleDate).isSameOrBefore(tomorrowEndDay));
};

const hasNormalJob = (scheduleDates: Date[]) => {
  const tomorrowEndDay = dayjs().add(1, 'day').endOf('day');
  return some(scheduleDates, (scheduleDate) => dayjs(scheduleDate).isAfter(tomorrowEndDay));
};

type JobPricingRequirement = {
  jobStartAt: dayjs.ConfigType;
  jobEndAt: dayjs.ConfigType;
  breakDurationMinute?: number;
  bidPricePerHour?: number;
  skillsId?: string[];
};

const JobCreatePage = () => {
  const goBack = useGoBack();

  const { contextCurrentRestaurant } = React.useContext(AppContext);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const datePickerRef = React.useRef<any>(null);

  useTrackPageView('NewJobPage');

  const [showDialog, setShowDialog] = React.useState(false);
  const [isNotEnoughCredit, setIsNotEnoughCredit] = React.useState(false);
  const openDialogHandler = () => setShowDialog(true);
  const closeDialogHandler = () => setShowDialog(false);

  const [showRestaurantIncompleteDialog, setShowRestaurantIncomplete] = React.useState(false);
  const closeRestaurantIncompleteDialogHandler = () => setShowRestaurantIncomplete(false);
  const [selectedOptionBidPrice, setSelectedOptionBidPrice] = useState<BidPriceOptionType>('BID_PRICE_OPTION_DEFAULT');

  React.useEffect(() => {
    if (contextCurrentRestaurant?.isInfoComplete === false) {
      setShowRestaurantIncomplete(true);
    }
  }, [contextCurrentRestaurant]);
  const {
    control,
    getValues,
    getFieldState,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: {
      isSubmitting,
      touchedFields: { duties: dutiesTouched },
      errors: validateErrors,
    },
  } = useForm<JobCreateFormData>({
    defaultValues: {
      scheduleDates: [],
      breakDurationMinute: 60,
      gender: 'SET_NULL_ON_SUBMIT',
      jobType: 'other',
      duties: [],
      numberOfPosition: 1,
      restaurantBidPricePerHour: null,
      skillIds: [],
    },
    resolver: yupResolver(JobCreateSchema),
  });

  const scheduleDates = watch('scheduleDates');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const breakDurationMinute = watch('breakDurationMinute');
  const jobType = watch('jobType');
  const skillIds = watch('skillIds');

  const { data: jobCreatingOptionData, loading: jobCreatingOptionLoading } = useQuery(jobCreatingOptionGql, {
    variables: { businessType: contextCurrentRestaurant?.businessType },
    skip: !contextCurrentRestaurant?.businessType,
  });

  const [pricingRequirements, setPricingRequirements] = useState<JobPricingRequirement[]>([]);
  const deferredPricingRequirements = useDeferredValue(pricingRequirements);

  const {
    data: pricingData,
    loading: pricingLoading,
    variables: pricingVariables,
  } = useQuery(jobPricing, {
    variables: {
      requirements: deferredPricingRequirements,
    },
    skip:
      isNil(contextCurrentRestaurant) ||
      isEmpty(scheduleDates) ||
      isNil(startTime) ||
      isNil(endTime) ||
      isNil(breakDurationMinute),
  });
  const [jobPricingNormalQuery] = useLazyQuery(jobPricing);

  const [minimumBid, setMinimumBid] = useState<number>(0);
  const [customBidPrice, setCustomBidPrice] = useState<number>(0);
  const [priceRangeNormal, setPriceRangeNormal] = useState<{ min: number | null; max: number | null }>({
    min: null,
    max: null,
  });
  const [priceRange, setPriceRange] = useState<{ min: number | null; max: number | null }>({ min: null, max: null });

  useEffect(() => {
    const nextPricingRequirements = map(scheduleDates, (date) => {
      const jobStartAt = dayjs(date)
        .set('hour', dayjs(startTime).hour())
        .set('minute', dayjs(startTime).minute())
        .set('second', 0);
      let jobEndAt = dayjs(date)
        .set('hour', dayjs(endTime).hour())
        .set('minute', dayjs(endTime).minute())
        .set('second', 0);

      if (jobEndAt.isBefore(jobStartAt)) {
        jobEndAt = jobEndAt.add(1, 'day');
      }

      const bidPricePerHour = selectedOptionBidPrice === 'BID_PRICE_OPTION_CUSTOM' ? customBidPrice : undefined;

      return { jobStartAt, jobEndAt, breakDurationMinute, bidPricePerHour, skillIds };
    });

    setPricingRequirements(nextPricingRequirements);
  }, [scheduleDates, startTime, endTime, breakDurationMinute, customBidPrice, selectedOptionBidPrice, skillIds]);

  useEffect(() => {
    if (!pricingLoading && pricingData) {
      const nextPriceRange = {
        min: max(map(pricingData?.jobPricing, (p) => p.minTotalPrice as number)) ?? null,
        max: max(map(pricingData?.jobPricing, (p) => p.maxTotalPrice as number)) ?? null,
      };

      setPriceRange(nextPriceRange);
      setMinimumBid(max(map(pricingData?.jobPricing, (p) => p.minBidAllowed as number)) ?? 0);

      // Check is current pricingData is bid price. if true, Query additional non bid price pricingData
      if (some(pricingVariables?.requirements, 'bidPricePerHour')) {
        const pricingRequirementsNormal = map(pricingVariables?.requirements, (requirement) => ({
          ...requirement,
          bidPricePerHour: undefined,
        }));

        jobPricingNormalQuery({
          variables: { requirements: pricingRequirementsNormal },
          fetchPolicy: 'cache-first',
        }).then(({ data: pricingDataNormal }) => {
          setPriceRangeNormal({
            min: max(map(pricingDataNormal?.jobPricing, (p) => p.minTotalPrice as number)) ?? null,
            max: max(map(pricingDataNormal?.jobPricing, (p) => p.maxTotalPrice as number)) ?? null,
          });
        });
      } else {
        setPriceRangeNormal(nextPriceRange);
      }
    }
  }, [pricingLoading, pricingData]);

  const maxNumberOfPosition = jobCreatingOptionData?.jobCreatingOption?.maxNumberOfPosition ?? 20;
  const otHour = jobCreatingOptionData?.jobCreatingOption?.otHour ?? 0;

  const jobTypeOptionWithOther = React.useMemo(
    () =>
      some(jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties, ({ id }) => id === 'other')
        ? jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties
        : concat(jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties ?? [], {
            id: 'other',
            duties: [],
            defaultDuties: [],
          }),
    [jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties],
  );
  const jobDutyOptions = find(jobTypeOptionWithOther, (jobAndDuty) => jobAndDuty.id === jobType);
  const urgentPricing = reduce(
    jobCreatingOptionData?.jobCreatingOption?.pricing,
    (prev, { priceType, price }) => ({ ...prev, [priceType]: price }),
    {},
  );
  const cancelFees = reduce(
    jobCreatingOptionData?.jobCreatingOption?.cancelFee,
    (prev, { priceType, price }) => ({ ...prev, [priceType]: price }),
    {},
  );
  const { cashbacks: rawCashbacks } = useRestaurantCashbacks(contextCurrentRestaurant?.id, {
    fetchPolicy: 'network-only',
  });
  const cashbacks = React.useMemo(() => filterAvailableCashbacks(rawCashbacks), [rawCashbacks]);

  const numberOfPositionHandler = (positionNumber: number | null) => {
    if (isNil(positionNumber)) {
      // @ts-expect-error Let user fix this by enter something to form.
      setValue('numberOfPosition', null);
      return;
    }

    let nextValue = positionNumber;
    if (!Number.isFinite(nextValue)) {
      return;
    }

    if (maxNumberOfPosition !== -1) {
      nextValue = clamp(nextValue, 0, maxNumberOfPosition);
    } else {
      nextValue = nextValue < 0 ? 0 : nextValue;
    }
    setValue('numberOfPosition', nextValue);
  };

  React.useEffect(() => {
    if (contextCurrentRestaurant === null) return;

    const stash = Stash(CREATE_JOB);
    const key = contextCurrentRestaurant.id.toString();
    const values = stash.load(key);

    // monkey patch
    if (values.startTime) values.startTime = dayjs(values.startTime);
    if (values.endTime) values.endTime = dayjs(values.endTime);

    if (values.jobType && !find(jobTypeOptionWithOther, ({ id }) => id === values.jobType)) {
      values.jobType = 'other';
    }

    reset(values, { keepDefaultValues: true });
  }, [contextCurrentRestaurant, reset, jobTypeOptionWithOther]);

  React.useEffect(() => {
    if (jobType === 'other' || isNil(jobType)) {
      setValue('duties', []);
    }

    if (!dutiesTouched) {
      setValue('duties', jobDutyOptions?.defaultDuties ?? []);
    }
  }, [dutiesTouched, jobDutyOptions?.defaultDuties, jobType, setValue]);

  const jobDutiesHandler = (checked: boolean, value: string) => {
    let newDuties = getValues('duties');
    if (checked) {
      newDuties = uniq([value, ...newDuties]);
    } else {
      newDuties = filter(newDuties, (duty) => duty !== value);
    }
    setValue('duties', newDuties, { shouldTouch: true });
  };

  const [createJobMutation] = useMutation(createJobGql);
  const [errorMessagePerDate, setErrorMessagePerDate] = React.useState<{ date: Date; errorMessage: string | null }[]>(
    [],
  );
  const [hasCreateFailure, setHasCreateFailure] = React.useState(false);

  React.useEffect(() => {
    const subscription = watch(() => {
      if (!hasCreateFailure) return;
      setHasCreateFailure(false);
      setErrorMessagePerDate([]);
    });
    return () => subscription.unsubscribe();
  }, [watch, hasCreateFailure]);

  const prefillFormData = (prefillData: JobCreateFormData) => {
    if (contextCurrentRestaurant === null) return;

    const stash = Stash(CREATE_JOB);
    const key = contextCurrentRestaurant.id.toString();
    const values = pick(prefillData, [
      'startTime',
      'endTime',
      'breakDurationMinute',
      'gender',
      'jobType',
      'duties',
      'remark',
      'detail',
      'numberOfPosition',
      'uniform',
      'benefit',
      'skillIds',
      'uniformId',
    ]);
    stash.save(key, values);
  };

  const submitHandler: SubmitHandler<JobCreateFormData> = async (values) => {
    if (!contextCurrentRestaurant?.id) return;
    if (hasCreateFailure) return;

    if (contextCurrentRestaurant.isInfoComplete === false) {
      setShowRestaurantIncomplete(true);
      return;
    }

    setErrorMessagePerDate([]);
    let nextErrorMessagePerDate: { date: Date; errorMessage: string | null }[] = [];

    const uniformId = values.uniformId === '_OTHER_UNIFORM' ? null : values.uniformId;
    const uniformText = values.uniformId === '_OTHER_UNIFORM' ? values.uniform : null;
    const remarkText = join(
      compact(values.jobType === 'other' ? [values.detail, uniformText, values.remark] : [uniformText, values.detail]),
      '\n\n',
    );

    const variable = {
      restaurantId: contextCurrentRestaurant.id.toString(),
      startTime: dayjs(values.startTime).format('HH:mm'),
      endTime: dayjs(values.endTime).format('HH:mm'),
      breakDurationMinute: values.breakDurationMinute,
      gender: values.gender === 'SET_NULL_ON_SUBMIT' ? null : values.gender,
      jobType: values.jobType,
      duties: values.jobType === 'other' ? [] : values.duties,
      uniformId,
      remark: remarkText,
      numberOfPosition: values.numberOfPosition,
      benefit: values.benefit,
      restaurantBidPricePerHour:
        selectedOptionBidPrice === 'BID_PRICE_OPTION_CUSTOM' ? values.restaurantBidPricePerHour : null,
      skillIds: values.skillIds,
    };
    // eslint-disable-next-line no-restricted-syntax
    for (const scheduleDate of values.scheduleDates) {
      try {
        const idempotencyKey = buildIdempotencyKey(`create-job-${contextCurrentRestaurant.id}`);
        // TODO: CACHE Update /jobs cache
        // eslint-disable-next-line no-await-in-loop
        const { data: jobData } = await createJobMutation({
          variables: {
            ...variable,
            scheduleDate: dayjs(scheduleDate).format('YYYY-MM-DD'),
          },
          context: {
            headers: {
              'x-idempotency-key': idempotencyKey,
            },
          },
        });

        if (!jobData?.createJob.success) {
          const errorMessage = joinPairs(jobData?.createJob.errors);
          nextErrorMessagePerDate = [...nextErrorMessagePerDate, { date: scheduleDate, errorMessage }];
          setHasCreateFailure(true);

          if (errorMessage.includes(walletErrorI18n('amount.not_enough'))) {
            setIsNotEnoughCredit(true);
            Snackbar.enqueueError(INSUFFICIENT_BALANCE);
          }
        } else {
          // TODO: avoid loop
          prefillFormData(values);
        }
      } catch (_error) {
        setHasCreateFailure(true);
        nextErrorMessagePerDate = [
          ...nextErrorMessagePerDate,
          { date: scheduleDate, errorMessage: jobCreatePageI18n('create_request_failed_message') },
        ];
      }
    }

    setErrorMessagePerDate(nextErrorMessagePerDate);

    if (isEmpty(nextErrorMessagePerDate)) {
      goBack();
    }
  };

  const uniformId = watch('uniformId');
  const jobTypedUniforms = React.useMemo(
    () => filter(jobCreatingOptionData?.jobCreatingOption?.uniforms, (uniform) => uniform.jobType === jobType),
    [jobCreatingOptionData?.jobCreatingOption?.uniforms, jobType],
  );
  const selectedUniform = React.useMemo(
    () => find(jobTypedUniforms, (uniform) => uniform.id === uniformId),
    [uniformId, jobTypedUniforms],
  );

  // Handle all set/clear uniformId base on avalible uniforms
  // * Show normal uniform text input if no avalible jobTyped uniforms
  // * Set saved uniformId if user not touch it yet and saved uniformId in avalible jobTyped uniforms
  // * Clear uniformId if currentUniformId not in jobTyped uniforms (but uniforms not empty)
  React.useEffect(() => {
    if (jobCreatingOptionLoading) return;

    const currentUniformId = getValues('uniformId');
    const currentUniformIdStatus = getFieldState('uniformId');
    const jobTypedUniformIds = map(jobTypedUniforms, 'id');

    const stashValues = contextCurrentRestaurant ? Stash(CREATE_JOB).load(toString(contextCurrentRestaurant.id)) : null;
    const savedUniformId: string | undefined = get(stashValues, 'uniformId');

    if (isEmpty(jobTypedUniforms)) {
      setValue('uniformId', '_OTHER_UNIFORM', { shouldValidate: true });
    } else if (
      !currentUniformIdStatus.isTouched &&
      !isNil(savedUniformId) &&
      includes(jobTypedUniformIds, savedUniformId)
    ) {
      setValue('uniformId', savedUniformId);
    } else if (!includes(jobTypedUniformIds, currentUniformId)) {
      // @ts-expect-error Reset uniformId to null. Make user select again.
      setValue('uniformId', null);
    }
  }, [jobCreatingOptionLoading, jobTypedUniforms, contextCurrentRestaurant]);

  const uniformErrorMessage = React.useMemo(
    () => (uniformId === '_OTHER_UNIFORM' ? validateErrors.uniform?.message : validateErrors.uniformId?.message),
    [uniformId, validateErrors.uniform, validateErrors.uniformId],
  );

  const findSkill = (skillId: string) =>
    find(jobCreatingOptionData?.jobCreatingOption?.skills, (skill) => skill.id === skillId);

  if (isNil(contextCurrentRestaurant?.businessType)) {
    return (
      <Container
        maxWidth={'sm'}
        sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Stack gap={1}>
          <Alert severity="warning">
            <AlertTitle>{i18n.t('errors.messages.restaurant_branch.blank')}</AlertTitle>
            {i18n.t('errors.messages.restaurant_branch.blank_detail')}
          </Alert>
          <RestaurantSelector
            variant="outlined"
            fullWidth
            sx={{ padding: 1 }}
            data-testid="restaurant-selector-warning"
          />
        </Stack>
      </Container>
    );
  }

  if (contextCurrentRestaurant?.isInfoComplete === false) {
    return (
      <Container
        maxWidth={'sm'}
        sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Stack gap={1}>
          <Alert severity="warning">
            <AlertTitle>{i18n.t('pages.restaurants_new.restaurant_incomplete')}</AlertTitle>
            {i18n.t('pages.restaurants_new.restaurant_incomplete_hint')}
          </Alert>
          <Button component={Link} to={`/restaurants/${contextCurrentRestaurant.id}/edit`} variant="outlined">
            {i18n.t('pages.restaurants_new.to_edit')}
          </Button>
        </Stack>
      </Container>
    );
  }

  if (jobCreatingOptionLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Protector permissions={['jobCreate']}>
        <Protector.Rejected>
          <Navigate to="/error" replace />
        </Protector.Rejected>
      </Protector>
      <Container maxWidth="sm" sx={{ paddingY: 2 }}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Grid container gap={2}>
            <Grid container item gap={1}>
              <Grid item xs={3}>
                <Stack minHeight={56} flexDirection={'row'} alignItems={'center'}>
                  <FormLabel error={isNil(contextCurrentRestaurant)}>{jobCreatePageI18n('restaurant')} *</FormLabel>
                </Stack>
              </Grid>
              <Grid container item xs={9} sx={{ marginRight: -1 }}>
                <RestaurantSelector fullWidth />
              </Grid>
            </Grid>

            <Controller
              key="scheduleDates"
              name="scheduleDates"
              control={control}
              render={({ field: { name, onChange, onBlur, value }, fieldState: { invalid, error } }) => (
                <FormControl id={name} fullWidth error={invalid}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('schedule_dates')} *</FormLabel>
                    </Grid>
                    <Grid item xs="auto">
                      <DatePicker
                        ref={datePickerRef}
                        multiple
                        name={name}
                        minDate={dayjs().startOf('day').toDate()}
                        maxDate={dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days').toDate()}
                        value={value}
                        onChange={(dates: DateObject[] | null) => {
                          onChange(map(dates, (date) => date.toDate()));
                          onBlur();
                        }}
                        render={
                          <IconButton data-testid="schedule-dates-icon">
                            <EditCalendar />
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs overflow={'auto'}>
                      <Button
                        color="inherit"
                        fullWidth
                        size="small"
                        onClick={() => datePickerRef.current?.openCalendar()}
                        sx={{ justifyContent: 'flex-start', minHeight: 48 }}
                      >
                        {isEmpty(scheduleDates) ? (
                          <Typography variant="subtitle2" textAlign={'start'} sx={{ opacity: 0.6 }}>
                            {jobCreatePageI18n('schedule_date_hint', { max_date: 12 })}
                          </Typography>
                        ) : (
                          <Stack direction={'row'} gap={1}>
                            {map(scheduleDates, (date, index) => (
                              <Box
                                key={index}
                                width={40}
                                height={40}
                                borderRadius={40}
                                flexShrink={0}
                                display={'inline-flex'}
                                justifyContent={'center'}
                                alignItems={'center'}
                                color={'primary.contrastText'}
                                bgcolor={'primary.main'}
                              >
                                <Typography>{dayjs(date).format('DD')}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container item gap={1} justifyContent={'flex-end'}>
                    <Grid item xs={9}>
                      <FormHelperText>
                        {hookFormNestedFieldErrorMessage(error, { joinErrorMessages: false })}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            <Grid container item gap={1}>
              <Grid item xs={3}>
                <Stack minHeight={56} flexDirection={'row'} alignItems={'center'}>
                  <FormLabel error={!isEmpty(validateErrors.startTime) || !isEmpty(validateErrors.endTime)}>
                    {jobCreatePageI18n('job_time')} *
                  </FormLabel>
                </Stack>
              </Grid>
              <Grid item container xs>
                <Controller
                  key="startTime"
                  name="startTime"
                  control={control}
                  render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                    <FormControl id={field.name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs="auto">
                          <FormLabel>{jobCreatePageI18n('job_time_from')}</FormLabel>
                        </Grid>
                        <Grid item xs>
                          <MobileTimePicker
                            ampm={false}
                            closeOnSelect
                            minutesStep={30}
                            value={value ?? null}
                            slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
                            {...field}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <FormHelperText>{error?.message}</FormHelperText>
                      </Grid>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item container xs>
                <Controller
                  key="endTime"
                  name="endTime"
                  control={control}
                  render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                    <FormControl id={field.name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs="auto">
                          <FormLabel>{jobCreatePageI18n('job_time_to')}</FormLabel>
                        </Grid>
                        <Grid item xs>
                          <MobileTimePicker
                            ampm={false}
                            closeOnSelect
                            minutesStep={30}
                            value={value ?? null}
                            slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
                            {...field}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <FormHelperText>{error?.message}</FormHelperText>
                      </Grid>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            <Controller
              key="breakDurationMinute"
              name="breakDurationMinute"
              control={control}
              render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                <FormControl id={name} fullWidth error={invalid}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('break_duration')} *</FormLabel>
                    </Grid>
                    <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                      <RadioButtonGroup
                        id={name}
                        color="primary"
                        value={value}
                        onChangeValue={onChange}
                        onBlur={onBlur}
                        items={[
                          { label: jobFieldI18n('break_duration_hour.zero'), value: 0 },
                          { label: jobFieldI18n('break_duration_hour.one'), value: 60 },
                          { label: jobFieldI18n('break_duration_hour.one_and_half'), value: 90 },
                          { label: jobFieldI18n('break_duration_hour.two'), value: 120 },
                        ]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item gap={1} justifyContent={'flex-end'}>
                    <Grid item xs={9}>
                      <FormHelperText>{error?.message}</FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            <Controller
              key="skillIds"
              name="skillIds"
              control={control}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <FormControl fullWidth>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('skillIds')}</FormLabel>
                    </Grid>
                    <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                      <Select
                        id={name}
                        multiple
                        fullWidth
                        displayEmpty
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        renderValue={(selectedSkillIds) =>
                          isEmpty(value) ? (
                            <Typography color="GrayText">{jobFieldI18n('skills.unspecified')}</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedSkillIds.map((selectedSkillId) => {
                                const selectedSkill = findSkill(selectedSkillId);
                                return <Chip key={selectedSkill.id} label={selectedSkill.name} />;
                              })}
                            </Box>
                          )
                        }
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 224, // 48(ITEM_HEIGHT) * 4.5 + 8(ITEM_PADDING_TOP)
                              width: { xs: '100%', sm: 0 },
                            },
                          },
                        }}
                        SelectDisplayProps={{
                          style: {
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            marginTop: '4px',
                            marginBottom: '4px',
                            minHeight: '48px',
                          },
                        }}
                      >
                        {map(jobCreatingOptionData?.jobCreatingOption?.skills, (skill) => (
                          <MenuItem key={skill.id} value={skill.id} divider>
                            <Checkbox edge="start" checked={includes(value, skill.id)} />
                            <ListItemText
                              primary={`${skill.name} ( +${jobCreatePageI18n('pricing_per_hour', { price: skill.additionalWagePerHour, joinOutput: true })})`}
                              primaryTypographyProps={{
                                variant: 'subtitle2',
                                fontWeight: 'bold',
                                sx: { whiteSpace: 'normal' },
                              }}
                              secondary={skill.description}
                              secondaryTypographyProps={{
                                sx: { whiteSpace: 'normal' },
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            {!isEmpty(watch('scheduleDates')) && (
              <Controller
                key="restaurantBidPricePerHour"
                name="restaurantBidPricePerHour"
                control={control}
                render={({ field: { name, onBlur, onChange }, fieldState: { invalid, error } }) => {
                  const customAmountBidPriceHandler = (newValue: number | null) => {
                    if (selectedOptionBidPrice === 'BID_PRICE_OPTION_CUSTOM') {
                      const newBidPrice = newValue ? max([newValue, minimumBid]) ?? 0 : 0;
                      setCustomBidPrice(newBidPrice);
                      onChange(newBidPrice);
                    } else {
                      onChange(null);
                    }
                  };

                  const selectedBidPrice = watch('restaurantBidPricePerHour');

                  useEffect(() => {
                    if (selectedOptionBidPrice === 'BID_PRICE_OPTION_CUSTOM') {
                      setCustomBidPrice(minimumBid);
                    }
                  }, [selectedOptionBidPrice]);

                  return (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('bid_price.rate')} *</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          {!isEmpty(scheduleDates) && !isEmpty(startTime) && !isEmpty(endTime) ? (
                            <RadioButtonGroup
                              id={name}
                              color="primary"
                              value={selectedOptionBidPrice}
                              onChangeValue={(newValue) =>
                                setSelectedOptionBidPrice(newValue || 'BID_PRICE_OPTION_DEFAULT')
                              }
                              onBlur={onBlur}
                              items={[
                                {
                                  label: (
                                    <Stack direction="row" alignItems="center">
                                      <Collapse in={pricingLoading} orientation="horizontal" unmountOnExit>
                                        <LoadingSpinner size={16} BoxProps={{ paddingRight: 1 }} />
                                      </Collapse>
                                      {/* eslint-disable-next-line no-nested-ternary */}
                                      {isNil(priceRangeNormal.min) || isNil(priceRangeNormal.max)
                                        ? jobCreatePageI18n('dynamic_price')
                                        : priceRangeNormal.min === priceRangeNormal.max
                                          ? priceRangeNormal.max
                                          : `${priceRangeNormal.min} - ${priceRangeNormal.max}`}
                                    </Stack>
                                  ),
                                  value: 'BID_PRICE_OPTION_DEFAULT',
                                },
                                { label: jobFieldI18n('bid_price.custom'), value: 'BID_PRICE_OPTION_CUSTOM' },
                              ]}
                            />
                          ) : (
                            <FormLabel>{jobFieldI18n('bid_price.detail')}</FormLabel>
                          )}
                        </Grid>
                      </Grid>
                      {selectedOptionBidPrice === 'BID_PRICE_OPTION_CUSTOM' && (
                        <>
                          <Grid container item alignItems="center" gap={1} mt={1}>
                            <Grid item xs={3} />
                            <Grid item container xs alignItems="center" spacing={1}>
                              <Grid item>
                                <NumberTextField
                                  id={`${name}-custom`}
                                  inputMode="numeric"
                                  value={customBidPrice}
                                  onNumberChange={(v) => v && customAmountBidPriceHandler(v)}
                                  error={invalid}
                                  InputProps={{
                                    inputProps: { pattern: '[0-9]*', sx: { maxWidth: 64, textAlign: 'center' } },
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <IconButton
                                          disabled={minimumBid === customBidPrice}
                                          onClick={() => customAmountBidPriceHandler(customBidPrice - 1)}
                                        >
                                          <Remove />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton onClick={() => customAmountBidPriceHandler(customBidPrice + 1)}>
                                          <Add />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item>
                                <Typography variant="h6">
                                  {jobFieldI18n('bid_price.paid_total', {
                                    total: numeral(priceRange.min).format('0,0.00'),
                                  })}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container item gap={1} mt={1}>
                            <Grid item xs={3} />
                            <Grid item xs>
                              <Alert severity="warning">{jobCreatePageI18n('bid_price_warning')}</Alert>
                            </Grid>
                          </Grid>
                        </>
                      )}
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  );
                }}
              />
            )}

            <Controller
              key="numberOfPosition"
              name="numberOfPosition"
              control={control}
              render={({
                field: { value: numberOfPosition, onChange: _onChange, ...field },
                fieldState: { invalid, error },
              }) => (
                <FormControl id={field.name} fullWidth error={invalid}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('number_of_position')} *</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <NumberTextField
                        {...field}
                        id={field.name}
                        inputMode="numeric"
                        value={numberOfPosition.toString()}
                        onNumberChange={numberOfPositionHandler}
                        error={invalid}
                        InputProps={{
                          inputProps: { pattern: '[0-9]*', sx: { maxWidth: 64, textAlign: 'center' } },
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                disabled={(numberOfPosition ?? 0) <= 1}
                                onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) - 1)}
                                data-testid="number-of-position-decrement"
                              >
                                <Remove />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disabled={maxNumberOfPosition !== -1 && (numberOfPosition ?? 0) >= maxNumberOfPosition}
                                onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) + 1)}
                                data-testid="number-of-position-increment"
                              >
                                <Add />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item gap={1} justifyContent={'flex-end'}>
                    <Grid item xs={9}>
                      <FormHelperText>{error?.message}</FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            <Controller
              key="gender"
              name="gender"
              control={control}
              render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                <FormControl id={name} fullWidth error={invalid}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('gender')} *</FormLabel>
                    </Grid>
                    <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                      <RadioButtonGroup
                        id={name}
                        color="primary"
                        value={value}
                        onChangeValue={onChange}
                        onBlur={onBlur}
                        items={[
                          { label: labelFieldEnum('job.genders', 'male'), value: 'male' },
                          { label: labelFieldEnum('job.genders', 'female'), value: 'female' },
                          { label: labelFieldEnum('job.genders', 'unspecified'), value: 'SET_NULL_ON_SUBMIT' },
                        ]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item gap={1} justifyContent={'flex-end'}>
                    <Grid item xs={9}>
                      <FormHelperText>{error?.message}</FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            <Controller
              key="jobType"
              name="jobType"
              control={control}
              render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                <FormControl id={name} fullWidth error={invalid}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('job_type')} *</FormLabel>
                    </Grid>
                    <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                      <RadioButtonGroup
                        id={name}
                        color="primary"
                        value={value}
                        onChangeValue={onChange}
                        onBlur={onBlur}
                        items={map(jobTypeOptionWithOther, (jobAndDuty) => ({
                          label: labelFieldEnum('job.job_types', jobAndDuty.id),
                          value: jobAndDuty.id,
                        }))}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item gap={1} justifyContent={'flex-end'}>
                    <Grid item xs={9}>
                      <FormHelperText>{error?.message}</FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />

            <Collapse key="duties" in={!isNil(jobType) && jobType !== 'other'} sx={{ width: '100%', marginBottom: -2 }}>
              <Controller
                key="duties"
                name="duties"
                control={control}
                render={({ field: { name, value: duties }, fieldState: { invalid, error } }) => (
                  <FormControl id={name} fullWidth error={invalid}>
                    <Grid container item gap={1}>
                      <Grid item xs={12}>
                        <FormLabel>{jobFieldI18n('duty')} *</FormLabel>
                      </Grid>
                      <Grid item xs={12}>
                        <FormGroup row>
                          {map(jobDutyOptions?.duties, (duty) => (
                            <Grid key={duty} item xs={12} sm={6}>
                              <FormControlLabel
                                label={labelFieldEnum('job.duties', duty)}
                                value={duty}
                                control={
                                  <Checkbox
                                    checked={includes(duties, duty)}
                                    onChange={({ target: { checked } }) => jobDutiesHandler(checked, duty)}
                                  />
                                }
                                sx={{ paddingLeft: 2 }}
                              />
                            </Grid>
                          ))}
                        </FormGroup>
                      </Grid>
                      <Grid container item>
                        <FormHelperText>{error?.message}</FormHelperText>
                      </Grid>
                    </Grid>
                  </FormControl>
                )}
              />
            </Collapse>

            <Collapse key="remark" in={isNil(jobType) || jobType === 'other'} sx={{ width: '100%', marginTop: -2 }}>
              <Controller
                key="remark"
                name="remark"
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                  <FormControl id={field.name} fullWidth error={invalid}>
                    <Grid container item gap={1}>
                      <FormLabel>{jobFieldI18n('remark')} *</FormLabel>
                      <TextField
                        id={field.name}
                        type="text"
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={8}
                        error={invalid}
                        placeholder={join(jobCreatePageI18n('remark_hint'))}
                        helperText={error?.message}
                        {...field}
                      />
                    </Grid>
                  </FormControl>
                )}
              />
            </Collapse>

            <Controller
              key="detail"
              name="detail"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <FormControl id={field.name} fullWidth error={invalid}>
                  <Grid container item gap={1}>
                    <FormLabel>{jobFieldI18n('detail')} </FormLabel>
                    <TextField
                      id={field.name}
                      type="text"
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={8}
                      error={invalid}
                      placeholder={`ตัวอย่าง มีค่ามัดจำผ้ากันเปื้อน 100 บาท `}
                      helperText={error?.message}
                      {...field}
                    />
                  </Grid>
                </FormControl>
              )}
            />

            <FormControl fullWidth error={!isNil(uniformErrorMessage)}>
              <Grid container item>
                <Stack width="100%" gap={1}>
                  <FormLabel>{jobFieldI18n('uniform')} *</FormLabel>

                  <Collapse in={!isEmpty(jobTypedUniforms)}>
                    <Controller
                      key="uniformId"
                      name="uniformId"
                      control={control}
                      render={({ field: { value, ...field } }) => (
                        <Select
                          value={value ?? ''}
                          {...field}
                          fullWidth
                          SelectDisplayProps={{
                            style: {
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              paddingTop: '4px',
                              paddingBottom: '4px',
                              minHeight: '56px',
                            },
                          }}
                        >
                          {map(jobTypedUniforms, (uniform) => (
                            <MenuItem key={uniform.id} value={uniform.id} divider>
                              <ListItem disablePadding dense>
                                <ListItemAvatar>
                                  <Avatar src={uniform.photo}>
                                    <Person />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={uniform.title} />
                              </ListItem>
                            </MenuItem>
                          ))}

                          <MenuItem key="_OTHER_UNIFORM" value="_OTHER_UNIFORM">
                            <ListItem disablePadding dense>
                              <ListItemAvatar>
                                <Avatar>
                                  <Notes />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={i18n.t('general.custom')} />
                            </ListItem>
                          </MenuItem>
                        </Select>
                      )}
                    />
                  </Collapse>

                  <Collapse in={!isNil(selectedUniform)}>
                    {!isNil(selectedUniform) && <UniformCard uniform={selectedUniform} />}
                  </Collapse>

                  <Collapse in={uniformId === '_OTHER_UNIFORM'}>
                    <Controller
                      key="uniform"
                      name="uniform"
                      control={control}
                      render={({ field, fieldState: { invalid } }) => (
                        <TextField
                          id={field.name}
                          type="text"
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={8}
                          error={invalid}
                          {...field}
                        />
                      )}
                    />
                  </Collapse>
                </Stack>

                <FormHelperText>{uniformErrorMessage}</FormHelperText>
              </Grid>
            </FormControl>

            <Controller
              key="benefit"
              name="benefit"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <FormControl id={field.name} fullWidth error={invalid}>
                  <Grid container item gap={1}>
                    <FormLabel>{jobFieldI18n('benefit')} *</FormLabel>
                    <TextField
                      id={field.name}
                      type="text"
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={8}
                      error={invalid}
                      placeholder={join(jobCreatePageI18n('benefit_hint'))}
                      helperText={error?.message}
                      {...field}
                    />
                  </Grid>
                </FormControl>
              )}
            />

            {hasUrgentJob(scheduleDates) && (
              <Grid container item gap={1}>
                <Typography variant="subtitle1" color={'primary'} alignItems={'center'} display={'inline-flex'}>
                  <Star fontSize="small" sx={{ marginRight: 1 }} />
                  {jobCreatePageI18n('create_condition_title_urgent')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.87 }}>
                  {jobCreatePageI18n('create_condition_description_urgent_1')}
                  <Typography variant="body2" sx={{ textDecoration: 'underline' }} display="inline">
                    {jobCreatePageI18n('create_condition_description_urgent_2_underline')}
                  </Typography>
                  {jobCreatePageI18n('create_condition_description_urgent_3')}
                </Typography>

                <Grid container item justifyContent={'center'}>
                  <Grid container item xs={12} sm={10}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{jobCreatePageI18n('date_duration')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{jobCreatePageI18n('work_day')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{jobCreatePageI18n('wage')}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell rowSpan={7}>
                            <Typography variant="body2">{jobCreatePageI18n('next_day')}</Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.weekday')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_next_day_weekday'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_next_day_weekday_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_next_day_weekday_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.weekend')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_next_day_weekend'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_next_day_weekend_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_next_day_weekend_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.holiday')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_next_day_holiday'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_next_day_holiday_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_next_day_holiday_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>

                      <TableBody>
                        <TableRow>
                          <TableCell rowSpan={7}>
                            <Typography variant="body2">{jobCreatePageI18n('same_day')}</Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.weekday')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_same_day_weekday'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_same_day_weekday_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_same_day_weekday_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.weekend')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_same_day_weekend'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_same_day_weekend_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_same_day_weekend_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">{i18n.t('datetime.day_of_week.holiday')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {jobCreatePageI18n('pricing_per_hour', {
                                price: get(urgentPricing, 'restaurant_hiring_same_day_holiday'),
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {otHour > 0 && get(urgentPricing, 'restaurant_hiring_same_day_holiday_ot') ? (
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('price_ot_rate')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {jobCreatePageI18n('pricing_per_hour', {
                                  price: get(urgentPricing, 'restaurant_hiring_same_day_holiday_ot'),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>

                <Grid container item gap={0.5}>
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    {jobCreatePageI18n('create_condition_description_urgent_4')}
                    <Typography variant="body2" sx={{ textDecoration: 'underline' }} display="inline">
                      {jobCreatePageI18n('create_condition_description_urgent_5_underline')}
                    </Typography>
                  </Typography>
                  {otHour > 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.87 }}>
                      {jobCreatePageI18n('create_condition_ot_rate', { ot_hour: otHour })}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" sx={{ opacity: 0.87 }}>
                    {jobCreatePageI18n('create_condition_description_urgent_6', {
                      cancel_fee: get(cancelFees, 'restaurant_cancel_same_day'),
                    })}{' '}
                    <LinkMui onClick={openDialogHandler} sx={{ '&:hover': { cursor: 'pointer' } }}>
                      <Typography
                        variant="body2"
                        color={'primary'}
                        sx={{ textDecoration: 'underline' }}
                        display="inline"
                        onClick={noop}
                      >
                        {i18n.t('general.see_more_detail')}
                      </Typography>
                    </LinkMui>
                  </Typography>
                </Grid>
              </Grid>
            )}

            {hasNormalJob(scheduleDates) && (
              <Grid container item gap={0.5}>
                <Typography variant="subtitle1" color="primary" alignItems="center" display="inline-flex" width="100%">
                  <Star fontSize="small" sx={{ marginRight: 1 }} />
                  {jobCreatePageI18n('create_condition_title_normal')}
                </Typography>

                {otHour > 0 ? (
                  <Typography variant="body2" sx={{ opacity: 0.87 }}>
                    {jobCreatePageI18n('create_condition_ot_rate', { ot_hour: otHour })}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ opacity: 0.87 }}>
                  {jobCreatePageI18n('create_condition_description_normal')}{' '}
                  <LinkMui onClick={openDialogHandler} sx={{ '&:hover': { cursor: 'pointer' } }}>
                    <Typography variant="body2" display="inline">
                      {i18n.t('general.see_more_detail')}
                    </Typography>
                  </LinkMui>
                </Typography>
              </Grid>
            )}

            <Collapse in={!isEmpty(validateErrors)} unmountOnExit sx={{ width: '100%' }}>
              <Alert severity="error" sx={{ width: '100%' }}>
                {map(validateErrors, (error, type) => (
                  <Typography key={type}>
                    • {jobFieldI18n(mapValueKeyToI18nField(type))}{' '}
                    {hookFormNestedFieldErrorMessage(error, { joinErrorMessages: false })}
                  </Typography>
                ))}
              </Alert>
            </Collapse>

            <Collapse in={!isEmpty(errorMessagePerDate)} unmountOnExit sx={{ width: '100%' }}>
              {map(errorMessagePerDate, ({ date, errorMessage }, index) => (
                <Alert key={index} severity="error">
                  {jobCreatePageI18n('error_message_per_date', {
                    date: dayjs(date).format('DD'),
                    error_message: errorMessage,
                  })}
                </Alert>
              ))}
            </Collapse>

            <Collapse in={hasCreateFailure} unmountOnExit sx={{ width: '100%' }}>
              <Alert severity="warning">{jobCreatePageI18n('create_request_failed_message')}</Alert>
            </Collapse>

            {isEmpty(cashbacks) ? null : (
              <Typography variant="body1" color="primary" sx={{ paddingY: 1 }}>
                {jobCreatePageI18n('cashbacks_available')}
              </Typography>
            )}

            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting || hasCreateFailure}
              loading={isSubmitting}
              data-testid="job-creation-submit-button"
            >
              {i18n.t('general.done')}
            </LoadingButton>
          </Grid>
        </form>
        <Dialog open={showDialog} onClose={closeDialogHandler} PaperProps={{ sx: { width: '100%' } }}>
          <DialogTitle flexDirection={'row'} alignItems={'center'} display={'inline-flex'}>
            <DialogTitle>{jobCreatePageI18n('create_condition_title')}</DialogTitle>
            <div style={{ flex: 1 }} />
            <Box>
              <IconButton onClick={closeDialogHandler}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container gap={2}>
              <Typography>
                {jobCreatePageI18n('create_condition_detail_1_1')}
                <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                  {jobCreatePageI18n('create_condition_detail_1_2_underline')}
                </Typography>
                {jobCreatePageI18n('create_condition_detail_1_3')}
              </Typography>
              <Typography>
                {jobCreatePageI18n('create_condition_detail_2_1')}
                <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                  {jobCreatePageI18n('create_condition_detail_2_2_underline')}
                </Typography>
                {jobCreatePageI18n('create_condition_detail_2_3')}
              </Typography>
              <Typography>
                {jobCreatePageI18n('create_condition_detail_3_1')}
                <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                  {jobCreatePageI18n('create_condition_detail_3_2_underline')}
                </Typography>
                {jobCreatePageI18n('create_condition_detail_3_3')}
              </Typography>
              <Typography>
                {jobCreatePageI18n('create_condition_detail_4_1')}
                <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                  {jobCreatePageI18n('create_condition_detail_4_2_underline')}
                </Typography>{' '}
                {jobCreatePageI18n('create_condition_detail_4_3')}
              </Typography>
              <Grid container item justifyContent={'center'}>
                <Grid item xs={12} sm={9}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{jobCreatePageI18n('date_duration')}</TableCell>
                        <TableCell>{jobFieldI18n('cancel_fee')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{jobCreatePageI18n('cancel_3_days')}</TableCell>
                        <TableCell>
                          {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_normal'),
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{jobCreatePageI18n('cancel_2_days')}</TableCell>
                        <TableCell>
                          {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_next_day'),
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{jobCreatePageI18n('cancel_1_day')}</TableCell>
                        <TableCell>
                          {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_same_day'),
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
              <Grid container item gap={2} paddingLeft={4}>
                <Typography>
                  {jobCreatePageI18n('create_condition_detail_41_1')}{' '}
                  {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                    cancel_fee: get(cancelFees, 'restaurant_cancel_normal'),
                  })}
                </Typography>
                <Typography>
                  {jobCreatePageI18n('create_condition_detail_42_1')}{' '}
                  {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                    cancel_fee: get(cancelFees, 'restaurant_cancel_next_day'),
                  })}
                </Typography>
                <Typography>
                  {jobCreatePageI18n('create_condition_detail_43_1')}{' '}
                  {jobCreatePageI18n('cancel_fee_per_time_per_position', {
                    cancel_fee: get(cancelFees, 'restaurant_cancel_same_day'),
                  })}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>

        <Dialog open={showRestaurantIncompleteDialog}>
          <DialogTitle>{jobCreatePageI18n('restaurant_incomplete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{jobCreatePageI18n('restaurant_incomplete_hint')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRestaurantIncompleteDialogHandler}>
              {jobCreatePageI18n('restaurant_incomplete_to_close')}
            </Button>
            <Button
              component={Link}
              to={`/restaurants/${contextCurrentRestaurant?.id}/edit`}
              onClick={closeRestaurantIncompleteDialogHandler}
            >
              {jobCreatePageI18n('restaurant_incomplete_to_edit')}
            </Button>
          </DialogActions>
        </Dialog>

        <CreditNotEnoughPopup open={isNotEnoughCredit} onClosed={(_) => setIsNotEnoughCredit(false)} />

        <BottomSafeArea />
      </Container>
    </>
  );
};

export default JobCreatePage;
